import { Component, Input, Output, EventEmitter, OnInit, OnChanges, ViewChild, OnDestroy } from '@angular/core';
import { MenuItem, MenuItemClickType, MenuItemType } from './menu-item';
import { Router } from '@angular/router';
import { UserImageComponent } from '../../../apps/shared/user-image/user-image.component';
import { NgClass } from '@angular/common';
import { CdkDragPlaceholder } from '@angular/cdk/drag-drop';
import { ServiceStatus } from '../../../apps/shared/service-status.enum';
import { UserService } from '../../../../services/user-service.service';
import { OktaAuthService } from '../../../../services/okta-auth.service';
import { User } from '../../../models/user.model';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

/**
 * An component representing any type of menu item.
 */
@Component({
    selector: 'menu-item',
    templateUrl: './menu-item.component.html',
    styleUrl: './menu-item.component.scss',
    imports: [NgClass, UserImageComponent, CdkDragPlaceholder, MatProgressSpinnerModule]
})
export class MenuItemComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('profileUserImage') profileUserImage!: UserImageComponent;

  @Input() menuItem!: MenuItem;
  @Input() isEditMode: boolean;
  /**
   * If the menu item is being used as a student app, use this for navigation
   */
  @Input() studentId?: number;
  /**If you want to not have click events happen*/
  @Input() disableClick: boolean = false;
  @Output() onIconClick = new EventEmitter<void>();
  @Output() onTitleClick = new EventEmitter<void>();
  @Output() onToggleVisibilityClick = new EventEmitter<void>();

  MenuItemType = MenuItemType;
  ServiceStatus = ServiceStatus;

  currentUserProfile: User | undefined = undefined;
  iconServiceStatus = ServiceStatus.unset;

  private destroy$ = new Subject<void>();

  constructor(private router: Router, private userService: UserService, private oktaAuthService: OktaAuthService) {
    this.currentUserProfile = this.userService.CurrentUserProfile;
  }

  ngOnInit(): void {
    // Subscribe to user profile changes to handle impersonation
    this.userService.currentUserProfile$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((profile) => {
      this.currentUserProfile = profile || undefined;
    });
  }

  ngOnChanges(): void {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**Get icon URL with caching to prevent change detection loops*/
  getIconUrl(): string {
    if (!this.menuItem.IconPath) {
      return '';
    }

    return this.menuItem.IconPath;
  }

  /**
   * Check if this item should show an app icon
   */
  shouldShowAppIcon(): boolean {
    // Handle both string and enum values for ItemType
    const itemType = this.menuItem?.ItemType;
    return itemType === MenuItemType.App;
  }

  handleIconClick(): void {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    this.onIconClick.emit();
    if (!this.disableClick && !this.isEditMode) {
      const itemType = this.menuItem.ItemType;
      if ((itemType === MenuItemType.App || itemType === MenuItemType.Profile) && this.menuItem.ClickPath) {
        if (this.menuItem.ClickType === MenuItemClickType.External) {
          // Fire-and-forget: launching may need to fetch a fresh token first.
          this.launchExternalApp();
        }
        else if (this.menuItem.ClickType === MenuItemClickType.Student) {
          // Student-type tiles get studentId from the hosting student page. On a logged-in
          // student's own main menu there is no hosting page, so fall back to their own id
          // (e.g. the Assessments tile).
          const studentId = this.studentId
            ?? (this.currentUserProfile?.UserType === 'Student' ? this.currentUserProfile.Id : undefined);
          if (studentId) {
            this.router.navigate(['/apps/students/' + studentId + '/' + this.menuItem.ClickPath]);
          }
        }
        else {
          // Main, and any retired value still sitting in a cached menu (e.g. the old Header = 2).
          // From a student's page, tell the target app which student it was opened for.
          const extras = this.studentId ? { queryParams: { studentUserId: this.studentId } } : undefined;
          this.router.navigate(['/apps/' + this.menuItem.ClickPath], extras);
        }
      }
      else if (itemType === MenuItemType.Student) {
        this.router.navigate(['/apps/students/' + this.menuItem.Id]);
      }
    }
  }

  /**
   * Launch an external link. When the app is configured to share authentication,
   * the user's Okta access token is POSTed to the destination (in the request body)
   * so they arrive already authenticated; otherwise the link opens normally.
   */
  private async launchExternalApp(): Promise<void> {
    if (!this.menuItem.ClickPath) {
      return;
    }

    let clickPath = this.menuItem.ClickPath;
    if (!clickPath.includes('http')) {
      clickPath = 'http://' + clickPath;
    }

    if (!this.menuItem.SendAuthToExternal) {
      window.open(clickPath, '_blank');
      return;
    }

    // Auth-sharing link: always open as a POST with the token in the body.
    // Refresh first so we hand over a full-lifetime token rather than one about to expire.
    await this.oktaAuthService.ensureFreshAccessToken();
    const token: any = await this.oktaAuthService.getOktaAuth().tokenManager.get('accessToken');
    const accessToken = (token && 'accessToken' in token) ? (token as any).accessToken : '';

    // The Okta user GUID comes from the ID token claims (sub). Tokens are unaffected by
    // impersonation, so this always identifies the real authenticated user.
    const idToken: any = await this.oktaAuthService.getOktaAuth().tokenManager.get('idToken');
    const oktaUserId = idToken?.claims?.sub ?? '';

    this.postToExternal(clickPath, accessToken, oktaUserId);
  }

  /**
   * Navigate to an external site via an auto-submitting POST form so the token
   * travels in the request body (never in the URL). Opens in a new tab/window.
   */
  private postToExternal(url: string, accessToken: string, oktaUserId: string): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.target = '_blank';
    form.setAttribute('rel', 'noopener');
    form.style.display = 'none';

    // Identity/analytics fields. okta_user_id is the user's Okta GUID (the ID token `sub`
    // claim); platform identifies the originating client (web / ios / android).
    const fields: Record<string, string> = {
      access_token: accessToken,
      okta_user_id: oktaUserId,
      platform: Capacitor.getPlatform(),
    };

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  handleTitleClick(): void {
    this.onTitleClick.emit();
  }

  handleToggleVisibilityClick(): void {
    this.onToggleVisibilityClick.emit();
  }

  getTestId(): string {
    const title = this.menuItem?.Title ?? '';
    const slug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return 'app-tile-' + slug;
  }
}
